
// import { Sortable, MultiDrag } from "sortablejs";
import draggable from "vuedraggable";
import { FormApi } from "@/services/api/Form.service";
import { getLookups } from "@/services/api/Lookup.service";
import { hasPermission } from "@/core/services/permission.service";
import {
  defineComponent,
  onMounted,
  ref,
  toRef,
  computed,
  watch,
  PropType,
  nextTick,
} from "vue";
import { TranslationService } from "@/core/services/TranslationService";
import { useToast } from "vue-toastification";
import { FormatMethods } from "@/helpers/data-format";
import { IForm, IFormSections } from "@/interfaces/iForm";
import FormSectionModal from "@/views/form/modal/SectionDetailModal.vue";
import AddEditModal from "@/components/modals/forms/AddEditModal.vue";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { LocationAPI } from "@/services/api/Location.service";
import { ILocation } from "@/interfaces/iLocation";
import { Modal } from "bootstrap";
import { SectionDesignAPI } from "@/services/api/SectionDesign.service";
import { Actions } from "@/store/enums/StoreEnums";
import * as Yup from "yup";
import store from "@/store";

export default defineComponent({
  props: {
    data: { type: Object as PropType<IForm>, required: true },
  },
  name: "create-form-modal",
  components: {
    draggable,
    FormSectionModal,
    AddEditModal,
  },
  // emits: ["on-submit"],
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const list1 = ref<Array<any>>([]);
    const selectedSection = ref<IFormSections>({
      webId: 0,
      name: "",
      status: "",
      locationName: "",
      locationCode: 0,
      locationCodeText: "",
      sequence: 0,
      recordHistory: false,
      locationRestriction: false,
      template: false,
      formDefinitionId: 0,
      formSectionId: 0,
    });
    const movedListSearch = ref<Array<any>>([]);
    const list2 = ref([{ name: "", id: 0, webId: 0 }]);
    const dataProp = toRef(props, "data");
    const allFormSections = ref({});
    const searchTermSections = ref<string>("");
    const searchSections = ref<string>("");
    const loading = ref<boolean>(false);
    const showDetailDialog = ref<any>(false);
    const moveAllForward = ref();
    const moveAllBackward = ref();
    const toast = useToast();
    const formSectionAddMessage = t("pages.form.formSectionAddMessage");
    const formSectionDeleteMessage = t("pages.form.formSectionDeleteMessage");
    const formSectionLocationMessage = t(
      "pages.form.formSectionLocationMessage"
    );
    const sectionUpdatedMessage = t("pages.form.sectionUpdatedMessage");
    const sectionMoveSelected = ref<Array<number>>([]);
    const sectionBackSelected = ref<Array<number>>([]);
    const newIndex = ref<any>();
    const showAddLocationModal = ref(false);
    const sectionLoading = ref(false);
    const options = ref<{ label: string; value: string }[]>([]);
    const siteOptions = ref<{ label: string; value: string }[]>([]);
    const userSiteModelData = ref({
      header: "Location",
      page: "Location",
      edit: false,
      data: {
        siteId: "",
        locationId: "",
        locationName: "",
        locationRestriction: false,
      },
      label: [{ addHeader: t("pages.location.assignLocation") }],
      model: [
        {
          name: "siteId",
          label: t("pages.site.title"),
          type: "list",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: siteOptions.value,
        },
        {
          name: "locationId",
          label: t("pages.location.location"),
          type: "list",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select location",
          options: options.value ?? [],
        },
        {
          name: "locationRestriction",
          label: t("pages.form.locationRestriction"),
          type: "checkbox",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
        },
      ],
    });
    const computedUserSiteModalData = computed(() => {
      return {
        ...userSiteModelData.value,
        model: userSiteModelData.value.model.map((model) => {
          if (model.name === "siteId") {
            return { ...model, options: siteOptions.value };
          }
          if (model.name === "locationId") {
            return { ...model, options: options.value ?? [] };
          }
          return model;
        }),
      };
    });
    const fetchLocationsList = async (id) => {
      const data = await LocationAPI.getInstance().getLocationList({
        page: 0,
        size: 1000,
        siteId: id,
      });
      options.value = data.content.map((item: ILocation) => ({
        label: item.name,
        value: `${item.webId}`,
      }));
    };
    const handleSiteChange = async (siteId) => {
      if (siteId) {
        options.value = [];
        await fetchLocationsList(siteId);
        await nextTick();
      }
    };

    // Always filter out already-selected sections and apply search filter
    const filteredSections = computed(() => {
      const selectedIds = list1.value.map(
        (item) => item.formSectionId ?? item.webId
      );
      let available = list2.value.filter(
        (item) => !selectedIds.includes(item.webId)
      );
      if (searchTermSections.value && searchTermSections.value.length >= 3) {
        const term = searchTermSections.value.trim().toLowerCase();
        available = available.filter((item) =>
          (item.name || "").toLowerCase().includes(term)
        );
      }
      return available;
    });
    const format = computed(() => {
      return FormatMethods;
    });
    const getAllFormSections = async () => {
      try {
        loading.value = true;
        const search = searchTermSections.value
          ? `?search=${searchTermSections.value}`
          : ``;
        const URL = `forms/sections/list${search}`;
        const res: any = await getLookups(URL);
        list2.value = res;
        moveAllBackward.value = false;
        moveAllForward.value = false;
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      getAllFormSections();
      getFormSection();
      await getLookups(
        `site/siteForms/${dataProp.value.webId}/sites?page=0&size=1000`
      )
        .then((res: any) => {
          siteOptions.value = res.content.map((data: any) => ({
            label: data.name,
            value: data.webId,
          }));
        })
        .catch((err) => {
          console.log(err);
        });
    });

    // getFormSection
    const getFormSection = async () => {
      await getLookups(
        "forms/definitionSections?formDefinitionId=" +
          dataProp.value.webId +
          "&status=ACTIVE"
      )
        .then((res: any) => {
          moveAllBackward.value = false;
          moveAllForward.value = false;
          checkSections(res.content);
        })
        .catch((err) => {
          return [];
        });
    };
    const checkSections = (sections) => {
      list1.value = [];
      sections.forEach((value) => {
        list1.value.push(value);
        list1.value.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
      });
      movedListSearch.value = [...list1.value];
    };

    //dragEnd
    const dragEnd = async (e) => {
      e.item.classList.remove("bg-light-warning");
      [...e.items, e.item].forEach((element) => {
        console.log(element);
        element.classList.remove("bg-light-warning");
        element.classList.add("warning");
      });
    };

    const openDetailModal = async (formSection) => {
      showDetailDialog.value = true;
      try {
        sectionLoading.value = true;
        const sectionData =
          await SectionDesignAPI.getInstance().getSectionDetail(
            formSection?.formSectionId ?? formSection.webId
          );
        selectedSection.value = sectionData;
      } finally {
        sectionLoading.value = false;
      }
    };
    const moveTo = () => {
      const items = list2.value.filter((item) =>
        sectionMoveSelected.value.includes(item.webId)
      );
      checkSections(items);
      list2.value
        .filter((item) => sectionMoveSelected.value.includes(item.webId))
        .forEach((x) => list2.value.splice(list2.value.indexOf(x), 1));

      if (sectionMoveSelected.value.length > 0) {
        const uniq = Array.from(new Set(sectionMoveSelected.value));
        const reqData = [...uniq].map((item) => {
          return {
            formSectionId: item,
            formDefinitionId: dataProp.value.webId,
            locationRestriction: false,
            sequence: newIndex.value,
          };
        });
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        FormApi.getInstance()
          .updateFormSections(reqData)
          .then(() => {
            getFormSection()
              .then(() => {
                toast.success(formSectionAddMessage);
                sectionMoveSelected.value = [];
              })
              .catch((err) => {
                console.error(err);
              });
          })
          .catch(() => {
            getAllFormSections();
            getFormSection();
          })
          .finally(() => {
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
          });
      }
    };
    //moveBack
    const moveBack = (e) => {
      if (
        e?.removed?.element &&
        sectionBackSelected.value.findIndex(
          (item) => item === e?.removed?.element.webId
        ) === -1
      ) {
        addBackSelectionMove(e?.removed?.element.webId);
      }
      const items = list1.value.filter((item) =>
        sectionBackSelected.value.includes(item.webId)
      );
      if (e?.added) {
        newIndex.value = e?.added?.newIndex;
      } else if (e?.removed) {
        newIndex.value = e?.removed?.newIndex;
      } else if (e?.moved) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        let webId = e?.moved?.element.webId;
        let payload = {
          oldIndex: e?.moved?.oldIndex,
          newIndex: e?.moved?.newIndex,
          formDefinitionId: e?.moved?.element.formDefinitionId,
        };
        FormApi.getInstance()
          .sortFormSectionSequence(webId, payload)
          .then(() => {
            getFormSection();
            getAllFormSections()
              .then(() => {
                toast.success(sectionUpdatedMessage);
              })
              .catch((err) => {
                console.error(err);
              });
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
          });
      }
      items.forEach((value, index) => {
        list2.value.push(value);
      });
      list1.value
        .filter((item) => sectionBackSelected.value.includes(item.webId))
        .forEach((x) => list1.value.splice(list1.value.indexOf(x), 1));
      //sectionBackSelected
      if (sectionBackSelected.value.length != 0) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        FormApi.getInstance()
          .deleteFormSections(sectionBackSelected.value)
          .then(() => {
            getFormSection();
            getAllFormSections()
              .then(() => {
                toast.success(formSectionDeleteMessage);
              })
              .catch((err) => {
                console.error(err);
              });
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
          });
        sectionBackSelected.value = [];
        moveAllBackward.value = false;
      }
    };
    //addToSelectionMove
    const addToSelectionMove = (webId) => {
      const index = sectionMoveSelected.value.findIndex(
        (item) => webId === item
      );
      if (index > -1) {
        sectionMoveSelected.value.splice(index, 1);
      } else {
        sectionMoveSelected.value.push(webId);
      }
    };
    //addBackSelectionMove
    const addBackSelectionMove = (webId) => {
      const index = sectionBackSelected.value.findIndex(
        (item) => webId === item
      );
      if (index > -1) {
        sectionBackSelected.value.splice(index, 1);
      } else {
        sectionBackSelected.value.push(webId);
      }
    };
    //changeAvailableSection
    const changeAvailableSection = (e) => {
      if (e?.removed?.element?.webId) {
        sectionMoveSelected.value = [
          ...(sectionMoveSelected.value ?? []),
          e.removed.element.webId,
        ];
        moveTo();
      }
    };

    const filteredSelectedSections = computed(() => {
      if (!searchSections.value || searchSections.value.length < 3) {
        return list1.value;
      }
      const term = searchSections.value.trim().toLowerCase();

      return list1.value.filter((item) =>
        (item.sectionName || item.name || "").toLowerCase().includes(term)
      );
    });

    const openLocationModal = async (element) => {
      userSiteModelData.value.edit = !!element.locationId;
      selectedSection.value = element;
      userSiteModelData.value.data = {
        siteId: "",
        locationId: "",
        locationName: "",
        locationRestriction: false,
      };
      options.value = [];

      if (element.locationId) {
        await handleSiteChange(element.siteId);
        userSiteModelData.value.data = {
          ...element,
          locationId: `${element.locationId}`,
          locationRestriction: element.locationRestriction,
        };
      }
      showAddLocationModal.value = true;
      const modal = new Modal(
        document.getElementById(
          "vz-add-edit-modal-add_edit_form_section_location_modal"
        ) as HTMLElement
      );
      modal.show();
    };
    const submitFormLocation = async (req) => {
      try {
        await FormApi.getInstance().updateFormSectionLocation(
          selectedSection.value?.webId,
          {
            ...req,
            formSectionId: selectedSection.value?.formSectionId ?? null,
            formDefinitionId: selectedSection.value?.formDefinitionId ?? null,
          }
        );
        toast.success(formSectionLocationMessage);
        getFormSection();
        showAddLocationModal.value = false;
      } catch (e) {
        console.log(e);
      }
    };
    watch(
      dataProp,
      (newValue) => {
        getAllFormSections();
        getFormSection();
        list1.value = [];
      },
      { deep: true }
    );
    //moveForward
    watch(moveAllForward, (newValue) => {
      if (newValue) {
        list2.value.forEach((item) => {
          //if (!sectionMoveSelected.value.includes(item.webId)) {
          sectionMoveSelected.value.push(item.webId);
          //}
        });
      } else {
        sectionMoveSelected.value = [];
      }
    });
    //moveBack
    watch(moveAllBackward, (newValue) => {
      if (newValue) {
        list1.value.forEach((item) => {
          //if (!sectionBackSelected.value.includes(item.webId)) {
          sectionBackSelected.value.push(item.webId);
          //}
        });
      } else {
        sectionBackSelected.value = [];
      }
    });
    watch(sectionMoveSelected, (newValue) => {
      if (
        sectionMoveSelected.value.length > 0 &&
        sectionMoveSelected.value.length === list2.value.length
      ) {
        moveAllForward.value = true;
      } else {
        moveAllForward.value = false;
      }
    });
    watch(sectionBackSelected, (newValue) => {
      if (
        sectionBackSelected.value.length > 0 &&
        sectionBackSelected.value.length === list1.value.length
      ) {
        moveAllBackward.value = true;
      } else {
        moveAllBackward.value = false;
      }
    });
    return {
      getAllFormSections,
      t,
      loading,
      allFormSections,
      searchTermSections,
      list1,
      list2,
      dragEnd,
      moveTo,
      sectionBackSelected,
      sectionMoveSelected,
      moveBack,
      addToSelectionMove,
      changeAvailableSection,
      addBackSelectionMove,
      searchSections,
      moveAllForward,
      moveAllBackward,
      filteredSections,
      filteredSelectedSections,
      openDetailModal,
      selectedSection,
      hasPermission,
      openLocationModal,
      showAddLocationModal,
      computedUserSiteModalData,
      submitFormLocation,
      sectionLoading,
      showDetailDialog,
      format,
      userSiteModelData,
      siteOptions,
      fetchLocationsList,
      options,
      handleSiteChange,
    };
  },
});
